import React, { useEffect, useRef, useState } from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import { Box, Collapse, FormControl, FormHelperText, IconButton } from '@mui/material';

import { sharedClasses } from './sharedClasses';
import { BLACK, WHITE } from '../../../ThemeContext/constants';

import type { Dispatch, SetStateAction } from 'react';

const MultiTagInput = ({
  tags,
  setTags,
  validation,
  validationMessage,
  label,
  required,
  error,
  width,
  helperText,
  styles,
  tagType,
  labelStylesOverride,
  helperTextStylesOverride,
  autoFocus,
  inputName,
  inputId,
  inputAutoComplete
}: {
  tags: string[];
  setTags: Dispatch<SetStateAction<string[]>>;
  validation?: (value: string) => boolean;
  validationMessage?: string;
  label: string;
  required?: boolean;
  error?: string;
  width?: string;
  helperText?: string;
  styles?: React.CSSProperties;
  tagType?: string;
  labelStylesOverride?: React.CSSProperties;
  helperTextStylesOverride?: React.CSSProperties;
  autoFocus?: boolean;
  inputName?: string;
  inputId?: string;
  inputAutoComplete?: string;
}) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const handleRemoveTag = (index: number) => {
    const newTags = [...tags];
    newTags.splice(index, 1);
    setTags(newTags);
  };

  const handleAddTag = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!inputValue) return;
    if (validation && !validation(inputValue)) {
      setMessage(validationMessage ?? 'Invalid item');
      return;
    }
    if (tags.includes(inputValue)) {
      setMessage('Items must be unique');
      return;
    }
    setTags([...tags, inputValue]);
    setInputValue('');
    setMessage('');
    (event.target as Element).setAttribute('size', '2');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === ',') {
      event.preventDefault();
      handleAddTag(event);
    } else if (event.key === 'Tab' && inputValue) {
      handleAddTag(event);
    } else if (event.key === 'Backspace' && inputValue === '') {
      event.preventDefault();
      if (selectedForDelete) {
        setTags((prev) => prev.slice(0, tags.length - 1));
        setMessage('');
        setSelectedForDelete(false);
      } else {
        setSelectedForDelete(true);
      }
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      setSelectedForDelete(false);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const paste = event.clipboardData?.getData('text')?.trim();
    if (!paste) return;

    event.preventDefault();
    const newTags = paste.split(/[\s,]+/);
    const uniqueTags = [...new Set(newTags)];
    const invalidTags = uniqueTags.filter((tag) => validation && !validation(tag));
    const validTags = uniqueTags.filter((tag) => {
      if (validation && !validation(tag)) return false;
      if (tags.includes(tag)) return false;
      return true;
    });
    setTags([...tags, ...validTags]);
    if (validation && invalidTags.length > 0) {
      setMessage(`Invalid ${tagType ?? 'tags'}: ${invalidTags.join(', ')}`);
    } else {
      setMessage('');
    }
    setInputValue('');
  };

  return (
    <FormControl sx={{ width: width ?? '290px' }} variant="standard">
      <Box sx={labelStylesOverride ?? sharedClasses.multiTagLabel}>
        {label}
        {required ? <span>*</span> : null}
      </Box>
      <Box
        onClick={() => inputRef.current?.focus()}
        sx={{
          ...sharedClasses.textAndTagsWrapper,
          ...(isFocused ? sharedClasses.focusedStyles : {}),
          ...(styles ?? {})
        }}
      >
        {tags.map((option: string, index: number) => (
          <Box
            key={option}
            sx={{
              ...sharedClasses.tag,
              ...(selectedForDelete && index + 1 === tags.length ? sharedClasses.tagSelected : {})
            }}
          >
            {option}
            <IconButton onClick={() => handleRemoveTag(index)} sx={sharedClasses.tagIconButton}>
              <ClearIcon sx={sharedClasses.tagIcon} />
            </IconButton>
          </Box>
        ))}
        <input
          ref={inputRef}
          autoComplete={inputAutoComplete}
          data-testid="multiple-tags-input"
          id={inputId}
          name={inputName}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          onPaste={(e) => handlePaste(e)}
          size={1}
          type="text"
          value={inputValue}
          onBlur={(e) => {
            handleAddTag(e);
            setIsFocused(false);
            setSelectedForDelete(false);
          }}
          onInput={(e) =>
            (e.target as HTMLInputElement).setAttribute(
              'size',
              `${((e.target as HTMLInputElement).value.length || 1) + 3}`
            )
          }
          style={{
            ...sharedClasses.textInput,
            caretColor: selectedForDelete ? WHITE : BLACK
          }}
        />
      </Box>
      {helperText ? (
        <FormHelperText sx={helperTextStylesOverride ?? sharedClasses.multiTagHelperText}>
          {helperText}
        </FormHelperText>
      ) : null}
      <Collapse in={!!(error ?? message)}>
        <Box sx={sharedClasses.fixedErrorBox}>{error ?? message}</Box>
      </Collapse>
    </FormControl>
  );
};

export default MultiTagInput;
